import type { FontData } from '../../types';
import { selectFontMetrics } from './selectFontMetrics';

const DEFAULT_ASCENT_RATIO = 0.8;
const DEFAULT_DESCENT_RATIO = 0.2;

export type TextLineGeometrySpan = {
  lineNumber: number;
  font?: FontData;
  fontSize: number;
  fontSizeOverride?: number;
  lineHeight?: number;
  lineHeightOverride?: number;
};

export type TextLineGeometry = {
  lineNumber: number;
  top: number;
  baseline: number;
  bottom: number;
  height: number;
  ascent: number;
  descent: number;
  glyphAscent: number;
  glyphDescent: number;
  capHeight: number;
};

type VerticalContributions = {
  ascent: number;
  descent: number;
  glyphAscent: number;
  glyphDescent: number;
  capHeight: number;
};

function getVerticalContributions(
  font: FontData | undefined,
  fontSize: number,
  lineHeight: number,
  fallbackFont?: FontData
): VerticalContributions {
  const metrics =
    (font ? selectFontMetrics(font) : null) ||
    (fallbackFont ? selectFontMetrics(fallbackFont) : null);
  const glyphAscent = metrics
    ? (fontSize * metrics.asc) / metrics.upm
    : fontSize * DEFAULT_ASCENT_RATIO;
  const glyphDescent = metrics
    ? (fontSize * Math.abs(metrics.des)) / metrics.upm
    : fontSize * DEFAULT_DESCENT_RATIO;
  const capHeight = metrics
    ? (fontSize * metrics.capHeight) / metrics.upm
    : glyphAscent * 0.7;
  const halfLeading = (fontSize * lineHeight - glyphAscent - glyphDescent) / 2;

  return {
    ascent: glyphAscent + halfLeading,
    descent: glyphDescent + halfLeading,
    glyphAscent,
    glyphDescent,
    capHeight,
  };
}

/**
 * Reconstruct CSS inline line boxes for mixed typography.
 *
 * The inherited box font contributes a parent strut only on lines containing
 * text without an explicit size. Each run then contributes its own
 * ascent/descent plus half-leading. Browser measurement remains the source of
 * truth for wrapping and horizontal positions.
 */
export function getTextLineGeometry({
  spans,
  defaultFont,
  defaultFontSize,
  lineHeight,
  paddingBetweenLines = 0,
}: {
  spans: TextLineGeometrySpan[];
  defaultFont: FontData;
  defaultFontSize: number;
  lineHeight: number;
  paddingBetweenLines?: number;
}): TextLineGeometry[] {
  if (spans.length === 0) {
    return [];
  }

  const effectiveLineHeight =
    Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : 1.2;
  const defaultContributions = getVerticalContributions(
    defaultFont,
    defaultFontSize,
    effectiveLineHeight
  );
  const spansByLine = new Map<number, TextLineGeometrySpan[]>();
  for (const span of spans) {
    const lineSpans = spansByLine.get(span.lineNumber) || [];
    lineSpans.push(span);
    spansByLine.set(span.lineNumber, lineSpans);
  }
  const lines: TextLineGeometry[] = [];
  let top = 0;

  for (const lineNumber of Array.from(spansByLine.keys()).sort(
    (a, b) => a - b
  )) {
    const lineSpans = spansByLine.get(lineNumber) || [];
    const usesDefaultStrut = lineSpans.some(
      (span) =>
        span.fontSizeOverride === undefined &&
        span.lineHeightOverride === undefined
    );
    const contributions = [
      ...(usesDefaultStrut ? [defaultContributions] : []),
      ...lineSpans.map((span) =>
        getVerticalContributions(
          span.font,
          span.fontSize,
          span.lineHeightOverride !== undefined &&
            Number.isFinite(span.lineHeight)
            ? Number(span.lineHeight)
            : effectiveLineHeight,
          defaultFont
        )
      ),
    ];
    const ascent = Math.max(...contributions.map((value) => value.ascent));
    const descent = Math.max(...contributions.map((value) => value.descent));
    const glyphAscent = Math.max(
      ...contributions.map((value) => value.glyphAscent)
    );
    const glyphDescent = Math.max(
      ...contributions.map((value) => value.glyphDescent)
    );
    const capHeight = Math.max(
      ...contributions.map((value) => value.capHeight)
    );

    const baseline = top + ascent;
    const bottom = baseline + descent;
    lines.push({
      lineNumber,
      top,
      baseline,
      bottom,
      height: ascent + descent,
      ascent,
      descent,
      glyphAscent,
      glyphDescent,
      capHeight,
    });
    top = bottom + paddingBetweenLines * 2;
  }

  return lines;
}
